/**
 * Production Line row background classes (all tabs).
 * Priority: GAP yellow → tab green → orange (holes/cutouts) → light blue (images).
 */

import { isOrderGAP } from './gapDetection';
import { orderHasImagesColumnData } from './orderTableHelpers';
import { orderHasDashboardRackFilled } from './productionLineHelpers';
import type { ProductionLineStage } from './types';
import type { OrderSummary } from './types';

export function orderHasHoleOrCutout(
  order: Pick<OrderSummary, 'hole_size_mm' | 'cutout_shape' | 'cutout_count'>
): boolean {
  return (
    order.hole_size_mm != null ||
    order.cutout_shape != null ||
    (order.cutout_count != null && order.cutout_count > 0)
  );
}

function getTabCompletionRowClass(
  order: OrderSummary,
  activeTab: ProductionLineStage
): string {
  const isScratched = order.metadata?.glass_arrival_workflow?.scratched ?? false;

  if (activeTab === 'dashboard') {
    return orderHasDashboardRackFilled(order) ? 'bg-green-100' : '';
  }

  if (activeTab === 'sealing_allocation') {
    const isSealed = order.metadata?.sealing_workflow?.sealed ?? false;
    return isSealed ? 'bg-green-100' : '';
  }

  if (activeTab === 'glass_cutting') {
    if (isScratched) return '';
    const isCut = order.metadata?.glass_cutting_workflow?.cut_completed ?? false;
    return isCut ? 'bg-green-100' : '';
  }

  if (activeTab === 'spacer_cutting') {
    if (isScratched) return '';
    const spacerDone = order.metadata?.spacer_workflow?.done ?? false;
    return spacerDone ? 'bg-green-100' : '';
  }

  if (activeTab === 'drawing') {
    if (isScratched) return '';
    const drawingDone = order.metadata?.drawing_workflow?.done ?? false;
    return drawingDone ? 'bg-green-100' : '';
  }

  return '';
}

type GlassValuesLookup = Map<string, { is_arrival: boolean; is_cutting: boolean }>;

export function getProductionLineRowClass(
  order: OrderSummary,
  activeTab: ProductionLineStage,
  glassValuesLookup?: GlassValuesLookup
): string {
  if (isOrderGAP(order, glassValuesLookup)) {
    return 'bg-yellow-100';
  }

  const tabClass = getTabCompletionRowClass(order, activeTab);
  if (tabClass) {
    return tabClass;
  }

  if (orderHasHoleOrCutout(order)) {
    return 'bg-orange-100';
  }

  if (orderHasImagesColumnData(order)) {
    return 'bg-blue-100';
  }

  return '';
}

export type RowColorLegendItem = {
  swatchClass: string;
  colorName: string;
  label: string;
};

const TAB_GREEN_LEGEND_LABELS: Record<ProductionLineStage, string> = {
  dashboard: 'Rack filled',
  sealing_allocation: 'Sealed',
  glass_cutting: 'Glass cut',
  spacer_cutting: 'Spacer done',
  drawing: 'Drawing done',
};

/** Row color guide for Production Line (priority order: yellow → green → orange → blue). */
export function getProductionLineRowColorLegend(
  activeTab: ProductionLineStage
): RowColorLegendItem[] {
  return [
    { swatchClass: 'bg-yellow-100', colorName: 'Yellow', label: 'GAP' },
    {
      swatchClass: 'bg-green-100',
      colorName: 'Green',
      label: TAB_GREEN_LEGEND_LABELS[activeTab],
    },
    { swatchClass: 'bg-orange-100', colorName: 'Orange', label: 'Hole / cutout' },
    { swatchClass: 'bg-blue-100', colorName: 'Blue', label: 'Image' },
  ];
}
